import { ColorsConstants } from './Colors.constants'
import {
  CMS_COLOR_PALETTE_TO_PATTERN,
  type ChartColorPalette,
} from './Colors.options'
import { COLOR_PATTERNS } from './Colors.patterns'
import type {
  ColorDomain,
  ColorPattern,
  ColorPatternName,
  ColorSemanticRole,
  MapDomainOptions,
  ResolvedColorPattern,
  SequentialOptions,
} from './Colors.types'

const defaultColorPattern: ColorPatternName = 'qualitative.default'

const mapColorPalette = (
  palette: ChartColorPalette | undefined,
): ColorPatternName =>
  palette == null ? defaultColorPattern : CMS_COLOR_PALETTE_TO_PATTERN[palette]

const parseHex = (hex: string): [number, number, number] | null => {
  const normalized = hex.startsWith('#') ? hex.slice(1) : hex
  const rgb =
    normalized.length === 8
      ? normalized.slice(0, 6)
      : normalized.length === 6
        ? normalized
        : null
  if (rgb == null || !/^[0-9a-fA-F]{6}$/.test(rgb)) return null
  return [
    parseInt(rgb.slice(0, 2), 16),
    parseInt(rgb.slice(2, 4), 16),
    parseInt(rgb.slice(4, 6), 16),
  ]
}

const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
  const channel = (value: number) => {
    const s = value / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

const contrastRatio = (a: string, b: string): number | null => {
  const rgbA = parseHex(a)
  const rgbB = parseHex(b)
  if (rgbA == null || rgbB == null) return null
  const l1 = relativeLuminance(rgbA)
  const l2 = relativeLuminance(rgbB)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

const getPattern = (name: ColorPatternName): ColorPattern => {
  const pattern = COLOR_PATTERNS[name]
  if (pattern == null) {
    throw new Error(`Colors.getPattern: unknown pattern "${name}"`)
  }
  return {
    ...pattern,
    colors: [...pattern.colors],
    roles: pattern.roles == null ? undefined : { ...pattern.roles },
  }
}

/** Highlight color for a series from its pattern (white for now). */
const getHighlight = (pattern: ColorPattern | ColorPatternName): string => {
  const resolved = typeof pattern === 'string' ? getPattern(pattern) : pattern
  return resolved.highlight
}

const getOverflow = (domain: ColorDomain, pattern: ColorPattern): number => {
  const maxDistinct = pattern.maxDistinct ?? pattern.colors.length
  return Math.max(0, domain.length - maxDistinct)
}

const mapDomain = (
  domain: ColorDomain,
  pattern: ColorPattern,
  options: MapDomainOptions = {},
): ResolvedColorPattern => {
  const { cycle = false, overrides = {} } = options
  const palette = pattern.colors
  const maxDistinct = pattern.maxDistinct ?? palette.length
  const overflowCount = getOverflow(domain, pattern)
  const byKey: Record<string, string> = {}

  // Sort for stable key→color assignment independent of input order.
  const sortedDomain = [...domain].sort((a, b) => a.localeCompare(b))

  sortedDomain.forEach((key, index) => {
    const override = overrides[key]
    if (override != null) {
      byKey[key] = override
      return
    }

    if (index >= maxDistinct && !cycle) {
      return
    }

    const paletteIndex = cycle ? index % palette.length : index
    const color = palette[paletteIndex]
    if (color != null) {
      byKey[key] = color
    }
  })

  return {
    colors: [...palette],
    byKey,
    overflowCount,
  }
}

const getSequential = (
  name: ColorPatternName = ColorsConstants.defaultSequentialPattern,
  options: SequentialOptions = {},
): readonly string[] => {
  const pattern = getPattern(name)
  if (pattern.intent !== 'sequential') {
    throw new Error(`Colors.getSequential: pattern "${name}" is not sequential`)
  }

  let steps = [...pattern.colors]
  if (options.reverse) {
    steps = steps.reverse()
  }
  if (options.steps != null && options.steps > 0) {
    steps = steps.slice(0, options.steps)
  }
  return steps
}

const getSemantic = (
  role: ColorSemanticRole,
  name: ColorPatternName = ColorsConstants.defaultSemanticPattern,
): string => {
  const pattern = getPattern(name)
  if (pattern.intent !== 'semantic') {
    throw new Error(`Colors.getSemantic: pattern "${name}" is not semantic`)
  }
  const color = pattern.roles?.[role]
  if (color == null) {
    throw new Error(
      `Colors.getSemantic: role "${role}" missing on pattern "${name}"`,
    )
  }
  return color
}

const meetsContrast = (
  foreground: string,
  background: string,
  minimumRatio = ColorsConstants.defaultContrastRatio,
): boolean => {
  const ratio = contrastRatio(foreground, background)
  if (ratio == null) return false
  return ratio >= minimumRatio
}

export const ColorsUtils = {
  parseHex,
  relativeLuminance,
  contrastRatio,
  getPattern,
  getHighlight,
  getOverflow,
  mapDomain,
  getSequential,
  getSemantic,
  meetsContrast,
  mapColorPalette,
}
